/**
 * Super Product Sheet - Bottom sheet for product detail in Super Inteligente
 * Shows large image, name, price, unit, description, stores availability, and add to cart
 */

import React, { useState, useEffect } from 'react';
import { X, Plus, Minus, Package, ShoppingCart } from 'lucide-react';
import api from '../../services/api';

const SuperProductSheet = ({ product, cartQty, onAdd, onQtyChange, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(cartQty || 1);

  useEffect(() => {
    loadDetail();
  }, [product.id]);

  useEffect(() => {
    setQuantity(cartQty || 1);
  }, [cartQty]);

  const loadDetail = async () => {
    try {
      const res = await api.get(`/super-inteligente/products/${product.id}`);
      setDetail(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleAdd = () => {
    const diff = quantity - cartQty;
    if (cartQty === 0) {
      onAdd();
      if (quantity > 1) {
        for (let i = 1; i < quantity; i++) onQtyChange(1);
      }
    } else if (diff > 0) {
      for (let i = 0; i < diff; i++) onQtyChange(1);
    } else if (diff < 0) {
      for (let i = 0; i < Math.abs(diff); i++) onQtyChange(-1);
    }
    onClose();
  };

  const unitLabel = product.unit === 'kg' ? 'por kilogramo' : product.unit === 'litro' ? 'por litro' : 'por pieza';

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center touch-none" onClick={onClose} style={{ overscrollBehavior: 'contain' }}>
      <div
        className="bg-white w-full md:max-w-lg md:rounded-2xl rounded-t-3xl max-h-[90vh] overflow-y-auto animate-slide-up overscroll-contain"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Product Image */}
        <div className="relative">
          {product.image_url ? (
            <img src={product.image_url} alt={product.name} className="w-full h-64 md:h-80 object-cover md:rounded-t-2xl" />
          ) : (
            <div className="w-full h-64 md:h-80 bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center md:rounded-t-2xl">
              <Package className="w-20 h-20 text-emerald-300" />
            </div>
          )}
          <button onClick={onClose} className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm p-2.5 rounded-full shadow-lg hover:bg-white transition">
            <X className="w-5 h-5 text-gray-700" />
          </button>
          {/* Unit badge */}
          <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow text-sm font-medium text-gray-700">
            {unitLabel}
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Name & Price */}
          <div>
            <h2 className="text-2xl font-extrabold text-gray-900">{product.name}</h2>
            {product.category && (
              <span className="inline-block mt-2 text-xs font-semibold text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full capitalize">
                {product.category.replace('_', ' ')}
              </span>
            )}
            {product.barcode && (
              <p className="text-xs text-gray-400 mt-2 font-mono">Código: {product.barcode}</p>
            )}
          </div>

          <div className="flex items-baseline gap-3">
            <span className="text-3xl font-black text-emerald-600">${product.unified_price?.toFixed(2)}</span>
            <span className="text-sm text-gray-400">/ {product.unit === 'kg' ? 'kg' : product.unit === 'litro' ? 'litro' : 'pieza'}</span>
          </div>

          {/* Description */}
          {product.description && (
            <p className="text-gray-600 text-sm leading-relaxed">{product.description}</p>
          )}

          {/* Availability */}
          {loading ? (
            <div className="bg-gray-50 rounded-xl p-4 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/2" />
            </div>
          ) : (() => {
            const count = detail?.available_stores?.length || 0;
            const level = count >= 3 ? 'high' : count >= 1 ? 'low' : 'none';
            const config = {
              high: { label: 'Alta disponibilidad', desc: 'Disponible en varias tiendas cercanas', color: 'bg-emerald-50 text-emerald-700', dot: 'bg-emerald-500' },
              low:  { label: 'Baja disponibilidad', desc: 'Pocas tiendas tienen este producto', color: 'bg-amber-50 text-amber-700', dot: 'bg-amber-500' },
              none: { label: 'Sin disponibilidad', desc: 'No encontramos este producto en tiendas cercanas', color: 'bg-red-50 text-red-600', dot: 'bg-red-500' },
            }[level];
            return (
              <div className={`rounded-xl p-4 ${config.color}`}>
                <div className="flex items-center gap-2.5">
                  <div className={`w-2.5 h-2.5 rounded-full ${config.dot} ${level !== 'none' ? 'animate-pulse' : ''}`} />
                  <span className="text-sm font-bold">{config.label}</span>
                </div>
                <p className="text-xs mt-1.5 opacity-75">{config.desc}</p>
              </div>
            );
          })()}

          {/* Quantity + Add */}
          <div className="border-t border-gray-100 pt-5 flex items-center gap-4">
            <div className="flex items-center gap-3 bg-gray-100 rounded-xl px-3 py-2">
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="p-1.5 hover:bg-gray-200 rounded-lg transition"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="font-bold text-lg w-8 text-center">{quantity}</span>
              <button
                onClick={() => setQuantity(quantity + 1)}
                className="p-1.5 hover:bg-gray-200 rounded-lg transition"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={handleAdd}
              className="flex-1 flex items-center justify-center gap-2 bg-emerald-600 text-white py-3.5 rounded-xl font-bold text-lg hover:bg-emerald-700 transition shadow-lg"
            >
              <ShoppingCart className="w-5 h-5" />
              {cartQty > 0 ? 'Actualizar' : 'Agregar'} ${(product.unified_price * quantity).toFixed(2)}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default SuperProductSheet;
